/**
 * PortalOffers — Manage specific offers within verticals
 *
 * An offer = a specific deal from a buyer for a vertical.
 * e.g., "Geico Auto Quote - $45/lead" under the auto_insurance vertical.
 * Multiple offers per vertical, engine picks the highest-paying one
 * that matches the user's profile.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Tag, Plus, Loader2, DollarSign, Pencil, Save, ArrowLeft, Trash2,
  Pause, Play, Phone, Mail, Globe, X,
} from 'lucide-react';
import { supabaseGet, supabasePost, supabasePatch, supabaseDelete } from '@/utils/supabaseRest';
import { useAuth } from '@/contexts/AuthContext';

interface Offer {
  id: string;
  vertical_id: string;
  buyer_id: string | null;
  name: string;
  description: string | null;
  payout: number;
  payout_type: string;
  route_type: string;
  route_url: string | null;
  daily_cap: number | null;
  monthly_cap: number | null;
  current_daily_count: number;
  accepted_states: string[];
  min_credit: string | null;
  status: string;
  priority: number;
  total_leads: number;
  total_revenue: number;
  conversion_rate: number;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
}

interface Buyer {
  id: string;
  name: string;
}

const VERTICALS = [
  'auto_insurance', 'home_insurance', 'health_insurance', 'life_insurance',
  'debt_relief', 'debt_consolidation', 'personal_loan', 'mortgage_refi',
  'solar', 'medicare', 'tax_relief', 'home_security', 'home_warranty',
  'personal_injury', 'reverse_mortgage',
];

export default function PortalOffers() {
  const { user } = useAuth();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Offer>>({});
  const [saving, setSaving] = useState(false);
  const [filterVertical, setFilterVertical] = useState<string>('all');

  const [formName, setFormName] = useState('');
  const [formVertical, setFormVertical] = useState('auto_insurance');
  const [formPayout, setFormPayout] = useState(50);
  const [formRouteType, setFormRouteType] = useState('lead');
  const [formRouteUrl, setFormRouteUrl] = useState('');
  const [formBuyerId, setFormBuyerId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, b] = await Promise.all([
        supabaseGet('offers?order=payout.desc'),
        supabaseGet('buyers?select=id,name&order=name.asc'),
      ]);
      setOffers(o || []);
      setBuyers(b || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!formName || !user?.id) return;
    setCreating(true);
    try {
      await supabasePost('offers', {
        user_id: user.id, name: formName, vertical_id: formVertical,
        payout: formPayout, payout_type: 'per_lead', route_type: formRouteType,
        route_url: formRouteUrl || null, buyer_id: formBuyerId || null,
        status: 'active', priority: 0,
      });
      setShowCreate(false); setFormName(''); setFormRouteUrl(''); load();
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const startEdit = (o: Offer) => { setEditingId(o.id); setEditForm({ ...o }); };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      const { id, created_at, current_daily_count, current_monthly_count, total_leads, total_revenue, conversion_rate, ...updates } = editForm as any;
      await supabasePatch(`offers?id=eq.${editingId}`, updates);
      setOffers(prev => prev.map(o => o.id === editingId ? { ...o, ...updates } : o));
      setEditingId(null);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const toggle = async (o: Offer) => {
    const s = o.status === 'active' ? 'paused' : 'active';
    await supabasePatch(`offers?id=eq.${o.id}`, { status: s }).catch(() => {});
    setOffers(prev => prev.map(x => x.id === o.id ? { ...x, status: s } : x));
  };

  const deleteOffer = async (id: string) => {
    await supabaseDelete(`offers?id=eq.${id}`).catch(() => {});
    setOffers(prev => prev.filter(o => o.id !== id));
  };

  const u = (k: string, v: any) => setEditForm(f => ({ ...f, [k]: v }));

  const filtered = filterVertical === 'all' ? offers : offers.filter(o => o.vertical_id === filterVertical);
  const totalPayout = offers.filter(o => o.status === 'active').reduce((s, o) => s + (o.payout || 0), 0);
  const activeOffers = offers.filter(o => o.status === 'active').length;

  // ── Full editor ──
  if (editingId) {
    return (
      <PortalLayout>
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={() => setEditingId(null)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
            <Button onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save
            </Button>
          </div>
          <h1 className="text-xl font-bold text-white">{editForm.name}</h1>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <F label="Offer Name" value={editForm.name} onChange={v => u('name', v)} />
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Vertical</label>
                  <select value={editForm.vertical_id} onChange={e => u('vertical_id', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                    {VERTICALS.map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <F label="Payout ($)" value={editForm.payout} onChange={v => u('payout', parseFloat(v) || 0)} />
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Payout Type</label>
                  <select value={editForm.payout_type} onChange={e => u('payout_type', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="per_lead">Per Lead</option>
                    <option value="per_call">Per Call</option>
                    <option value="per_click">Per Click</option>
                    <option value="rev_share">Revenue Share</option>
                    <option value="cpa">CPA</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Route Type</label>
                  <select value={editForm.route_type} onChange={e => u('route_type', e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="lead">Lead (POST form)</option>
                    <option value="call">Call (Ringba/phone)</option>
                    <option value="redirect">Redirect (URL)</option>
                    <option value="webhook">Webhook (POST JSON)</option>
                  </select>
                </div>
                <F label="Route URL" value={editForm.route_url} onChange={v => u('route_url', v)} placeholder="https://..." />
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Buyer</label>
                  <select value={editForm.buyer_id || ''} onChange={e => u('buyer_id', e.target.value || null)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="">No buyer (direct)</option>
                    {buyers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <F label="Priority (higher = preferred)" value={editForm.priority} onChange={v => u('priority', parseInt(v) || 0)} />
                <F label="Daily Cap" value={editForm.daily_cap} onChange={v => u('daily_cap', parseInt(v) || null)} placeholder="Unlimited" />
                <F label="Monthly Cap" value={editForm.monthly_cap} onChange={v => u('monthly_cap', parseInt(v) || null)} placeholder="Unlimited" />
              </div>
              <F label="Description" value={editForm.description} onChange={v => u('description', v)} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Min Credit</label>
                  <select value={editForm.min_credit || ''} onChange={e => u('min_credit', e.target.value || null)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="">Any</option>
                    <option value="poor">Poor+</option>
                    <option value="fair">Fair+</option>
                    <option value="good">Good+</option>
                    <option value="excellent">Excellent</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Accepted States</label>
                  <input value={(editForm.accepted_states || []).join(', ')} onChange={e => u('accepted_states', e.target.value.split(',').map((s: string) => s.trim().toUpperCase()).filter(Boolean))} placeholder="All states" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" />
                </div>
                <F label="Start Date" value={editForm.start_date?.slice(0, 10)} onChange={v => u('start_date', v || null)} placeholder="YYYY-MM-DD" />
                <F label="End Date" value={editForm.end_date?.slice(0, 10)} onChange={v => u('end_date', v || null)} placeholder="YYYY-MM-DD" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={editForm.status === 'active'} onChange={e => u('status', e.target.checked ? 'active' : 'paused')} className="rounded border-slate-600" />
                Active
              </label>

              {/* ── URL Parameters ── */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-white">URL Parameters</h4>
                  <button onClick={() => {
                    const params = editForm.url_parameters || [];
                    u('url_parameters', [...(params as any[]), { key: '', value: '', macro: '' }]);
                  }} className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 text-[10px] text-white hover:bg-emerald-700">
                    <Plus className="h-3 w-3" /> Add Param
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 mb-2">Appended to the offer route URL. Use macros like {'{session_id}'}, {'{affiliate_id}'}, {'{sub_id}'}, {'{email}'}, {'{phone}'}, {'{vertical}'}, {'{payout}'}, {'{click_id}'}</p>
                {((editForm.url_parameters || []) as any[]).length === 0 ? (
                  <div className="text-xs text-slate-600 py-2">No parameters. Click "Add Param" to start.</div>
                ) : (
                  <div className="space-y-1.5">
                    {((editForm.url_parameters || []) as any[]).map((p: any, i: number) => (
                      <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                        <input value={p.key || ''} onChange={e => { const params = [...((editForm.url_parameters || []) as any[])]; params[i] = { ...params[i], key: e.target.value }; u('url_parameters', params); }} placeholder="key (e.g. sub1)" className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                        <input value={p.value || ''} onChange={e => { const params = [...((editForm.url_parameters || []) as any[])]; params[i] = { ...params[i], value: e.target.value }; u('url_parameters', params); }} placeholder="value or {macro}" className="bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-white text-xs" />
                        <button onClick={() => u('url_parameters', ((editForm.url_parameters || []) as any[]).filter((_: any, j: number) => j !== i))} className="text-slate-600 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Preview URL */}
                {editForm.route_url && ((editForm.url_parameters || []) as any[]).length > 0 && (
                  <div className="mt-2 p-2 bg-slate-900 rounded text-[10px] text-emerald-400 font-mono break-all">
                    {editForm.route_url}?{((editForm.url_parameters || []) as any[]).map((p: any) => `${p.key || 'key'}=${p.value || 'value'}`).join('&')}
                  </div>
                )}
              </div>

              {/* ── Postback / S2S ── */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <h4 className="text-sm font-medium text-white mb-3">Postback / S2S Tracking</h4>
                <div className="space-y-3">
                  <F label="Postback URL (fired on conversion)" value={editForm.postback_url} onChange={v => u('postback_url', v)} placeholder="https://tracking.com/postback?click_id={click_id}&payout={payout}" />
                  <F label="S2S Conversion URL" value={editForm.s2s_url} onChange={v => u('s2s_url', v)} placeholder="https://network.com/s2s?txn_id={session_id}&amount={payout}" />
                  <p className="text-[10px] text-slate-500">Available macros: {'{session_id}'}, {'{click_id}'}, {'{affiliate_id}'}, {'{sub_id}'}, {'{payout}'}, {'{revenue}'}, {'{vertical}'}, {'{email}'}, {'{phone}'}, {'{offer_id}'}, {'{buyer_id}'}</p>
                </div>
              </div>

              {/* ── Field Mapping ── */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <h4 className="text-sm font-medium text-white mb-2">Field Mapping</h4>
                <p className="text-[10px] text-slate-500 mb-2">Map IntentDrive field names to the buyer's expected field names in the webhook payload.</p>
                <textarea value={JSON.stringify(editForm.field_mapping || {}, null, 2)} onChange={e => { try { u('field_mapping', JSON.parse(e.target.value)); } catch {} }} rows={5} placeholder={'{\n  "email": "lead_email",\n  "phone": "phone_number",\n  "full_name": "contact_name"\n}'} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-emerald-400 text-xs font-mono" />
              </div>

              {/* ── Custom Headers ── */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <h4 className="text-sm font-medium text-white mb-2">Custom Headers</h4>
                <textarea value={JSON.stringify(editForm.custom_headers || {}, null, 2)} onChange={e => { try { u('custom_headers', JSON.parse(e.target.value)); } catch {} }} rows={3} placeholder={'{\n  "X-API-Key": "your-key-here"\n}'} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-emerald-400 text-xs font-mono" />
              </div>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  // ── List ──
  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Tag className="h-6 w-6 text-emerald-400" /> Offers
            </h1>
            <p className="text-sm text-slate-400 mt-1">Manage specific deals within verticals. The engine picks the highest-paying offer per user.</p>
          </div>
          <Button onClick={() => setShowCreate(!showCreate)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-1" /> New Offer
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-white">{offers.length}</div><div className="text-xs text-slate-400">Total Offers</div></CardContent></Card>
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-emerald-400">{activeOffers}</div><div className="text-xs text-slate-400">Active</div></CardContent></Card>
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-amber-400">${totalPayout.toFixed(0)}</div><div className="text-xs text-slate-400">Total Payout Pool</div></CardContent></Card>
        </div>

        {/* Filter */}
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => setFilterVertical('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filterVertical === 'all' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>All</button>
          {[...new Set(offers.map(o => o.vertical_id))].map(v => (
            <button key={v} onClick={() => setFilterVertical(v)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filterVertical === v ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>{v.replace(/_/g, ' ')}</button>
          ))}
        </div>

        {showCreate && (
          <Card className="bg-slate-800/50 border-emerald-500/30">
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <F label="Offer Name *" value={formName} onChange={setFormName} placeholder="Geico Auto Quote" />
                <div><label className="text-xs text-slate-400 block mb-1">Vertical</label><select value={formVertical} onChange={e => setFormVertical(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm">{VERTICALS.map(v => <option key={v} value={v}>{v.replace(/_/g, ' ')}</option>)}</select></div>
                <F label="Payout ($)" value={formPayout} onChange={v => setFormPayout(parseFloat(v) || 0)} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs text-slate-400 block mb-1">Route Type</label><select value={formRouteType} onChange={e => setFormRouteType(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"><option value="lead">Lead</option><option value="call">Call</option><option value="redirect">Redirect</option><option value="webhook">Webhook</option></select></div>
                <F label="Route URL" value={formRouteUrl} onChange={setFormRouteUrl} placeholder="https://..." />
                <div><label className="text-xs text-slate-400 block mb-1">Buyer</label><select value={formBuyerId} onChange={e => setFormBuyerId(e.target.value)} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"><option value="">Direct (no buyer)</option>{buyers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
              </div>
              <div className="flex gap-2">
                <Button onClick={create} disabled={creating || !formName} className="bg-emerald-600 hover:bg-emerald-700">{creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Create</Button>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-emerald-400 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700"><CardContent className="p-8 text-center"><Tag className="h-10 w-10 text-slate-600 mx-auto mb-3" /><p className="text-sm text-slate-400">No offers yet.</p></CardContent></Card>
        ) : (
          <div className="space-y-2">
            {filtered.map(o => (
              <Card key={o.id} className={`border ${o.status === 'active' ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-900/50 border-slate-800 opacity-60'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                      {o.route_type === 'call' ? <Phone className="h-5 w-5 text-amber-400" /> : o.route_type === 'redirect' ? <Globe className="h-5 w-5 text-blue-400" /> : <Mail className="h-5 w-5 text-emerald-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white">{o.name}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-600/20 text-slate-400">{o.vertical_id.replace(/_/g, ' ')}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${o.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'}`}>{o.status}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3">
                        <span className="text-amber-400 font-medium">${o.payout}/{o.payout_type?.replace('per_', '')}</span>
                        <span>{o.route_type}</span>
                        {o.daily_cap && <span>{o.current_daily_count}/{o.daily_cap}/day</span>}
                        <span>{o.total_leads} leads</span>
                        <span>${o.total_revenue} rev</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => startEdit(o)} className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-600/20 text-xs text-emerald-400 hover:bg-emerald-600/30"><Pencil className="h-3 w-3" /> Edit</button>
                      <button onClick={() => toggle(o)} className="px-2 py-1 rounded bg-slate-700 text-xs text-slate-300 hover:bg-slate-600">{o.status === 'active' ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}</button>
                      <button onClick={() => deleteOffer(o.id)} className="text-slate-600 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}

function F({ label, value, onChange, placeholder }: { label: string; value: any; onChange: (v: string) => void; placeholder?: string }) {
  return <div><label className="text-xs text-slate-400 block mb-1">{label}</label><input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm" /></div>;
}
